import { useMemo } from 'react'

import qs from 'query-string'
import { formatDate, useError } from 'resources'
import { SINGLE_SIMULATED } from 'routes'

import { SIMULATED_QUERY, customMockExamsToSimulateds, enemMockExamsToSimulateds } from './graphql'
import { useMockExamsPlatformQuery } from 'resources/mock-exams-platform/use-mock-exams-platform-query'
import { formatPercentage, printMockExamGrade } from '../../utils'
import { useLocation } from 'react-router-dom'

export const useSimulated = () => {
  const [dispatchError] = useError()
  const { filter } = qs.parse(useLocation().search) || {}

  const {
    data = {
      userMockExams: {
        enemMockExams: [],
        customMockExams: [],
      },
    },
    loading,
    error,
  } = useMockExamsPlatformQuery(SIMULATED_QUERY, {
    fetchPolicy: 'no-cache',
  })

  const { enemMockExams = [], customMockExams = [] } = data.userMockExams

  const mockExamsAsSimulateds = useMemo(
    () => [
      ...enemMockExamsToSimulateds(enemMockExams).map(enemMockExamToCard(mapSimulatedsTitle)),
      ...customMockExamsToSimulateds(customMockExams).map(customMockExamToCard(mapSimulatedsTitle)),
    ].filter(Boolean).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [enemMockExams, customMockExams]
  )

  const filteredMockExamsAsSimulateds = useMemo(() => {
    if (!filter) return mockExamsAsSimulateds

    return mockExamsAsSimulateds.filter((simulated) => {
      const materias = simulated.data.find((item) => item.title === 'Matérias')?.value
      if (!materias) return false

      const normalizedMaterias = normalizeString(materias)

      return filter.split(',').some(filterItem =>
        normalizedMaterias.includes(normalizeString(filterItem.trim()))
      )
    })
  }, [filter, mockExamsAsSimulateds])

  if (error) {
    console.error(error)
    dispatchError(error)
  }

  return {
    simuladaoSimulateds: [],
    simulateds: filteredMockExamsAsSimulateds,
    loading,
    error,
  }
}

const mapSimulatedsTitle = (key) => {
  const fields = {
    timeInMinutes: 'Duração',
    disciplines: 'Matérias',
    numberOfQuestions: 'Questões',
    media: 'Resultado esperado',
    myGrade: 'Minha nota',
    createdAt: 'Data de Criação',
  }

  if (key in fields) return fields[key]
}

const toString = (disciplines) =>
  disciplines?.map((item) => item.name).join(', ') || ''

const toData = (fields, mapFields) => {
  if (!fields) return []

  let totalOfQuestions
  return Object.keys(fields).reduce((acc, key) => {
    const title = mapFields(key)

    if (!title) return acc

    if (key === 'timeInMinutes') {
      return acc.concat({ title, value: `${fields[key]} minutos` })
    }
    if (key === 'disciplines') {
      return acc.concat({ title, value: toString(fields[key]) })
    }
    if (key === 'numberOfQuestions') {
      totalOfQuestions = fields[key]
      return acc.concat({ title, value: fields[key] })
    }
    if (key === 'media') {
      return acc.concat({
        title,
        value: `${fields[key] || 0}/${totalOfQuestions}`,
      })
    }
    if (key === 'myGrade') {
      return acc.concat({
        title,
        value: fields[key],
      })
    }
    if (key === 'createdAt') {
      return acc.concat({ title, value: formatDate(fields[key]) })
    }

    return acc.concat({ title, value: fields[key] })
  }, [])
}

const buildURL = (slug) => SINGLE_SIMULATED.replace(':slug', slug)

const enemMockExamToCard = (fieldsMaper) => ({
  id,
  title,
  slug,
  status,
  description,
  __typename,
  url,
  myGrade,
  createdAt,
  ...rest
}) => ({
  id,
  title,
  url: url || buildURL(slug),
  slug,
  status,
  description,
  createdAt,
  data: toData({ ...rest, myGrade: printMockExamGrade(myGrade) }, fieldsMaper),
})

const customMockExamToCard = (fieldsMaper) => ({
  id,
  title,
  slug,
  status,
  myGrade,
  description,
  __typename,
  url,
  createdAt,
  ...rest
}) => ({
  id,
  title,
  url: url || buildURL(slug),
  slug,
  status,
  description,
  createdAt,
  data: toData({
    ...rest,
    myGrade: formatPercentage(myGrade),
  }, fieldsMaper),
})

const normalizeString = (str) => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^a-z0-9\s]/g, '') // Remove caracteres especiais
    .trim()
}
